import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, RotateCcw } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

interface FailedRow {
  id: string;
  unique_id: string;
  operation: string;
  attempt_count: number;
  failed_at: string | null;
  last_error: string | null;
  tenant_id: string;
}

interface Counters {
  pending: number;
  failed7d: number;
  oldestPendingAt: string | null;
}

export function S3MetadataOutboxHealth() {
  const [counters, setCounters] = useState<Counters>({ pending: 0, failed7d: 0, oldestPendingAt: null });
  const [failedRows, setFailedRows] = useState<FailedRow[]>([]);
  const [tenantSlugById, setTenantSlugById] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [pendingRes, failedCountRes, oldestRes, recentFailedRes, tenantsRes] = await Promise.all([
        supabase.from("s3_metadata_outbox").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("s3_metadata_outbox").select("id", { count: "exact", head: true }).eq("status", "failed").gte("failed_at", sevenDaysAgo),
        supabase.from("s3_metadata_outbox").select("created_at").eq("status", "pending").order("created_at", { ascending: true }).limit(1).maybeSingle(),
        supabase.from("s3_metadata_outbox").select("id, unique_id, operation, attempt_count, failed_at, last_error, tenant_id").eq("status", "failed").order("failed_at", { ascending: false }).limit(20),
        supabase.from("tenants").select("id, slug"),
      ]);

      setCounters({
        pending: pendingRes.count ?? 0,
        failed7d: failedCountRes.count ?? 0,
        oldestPendingAt: (oldestRes.data as { created_at?: string } | null)?.created_at ?? null,
      });
      setFailedRows((recentFailedRes.data as FailedRow[]) ?? []);
      const map: Record<string, string> = {};
      for (const t of (tenantsRes.data as { id: string; slug: string }[] | null) ?? []) {
        map[t.id] = t.slug;
      }
      setTenantSlugById(map);
    } catch (err) {
      toast({
        title: "Failed to load outbox health",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const retry = async (id: string) => {
    setRetryingId(id);
    try {
      const { error } = await supabase.rpc("retry_s3_metadata_outbox", { row_id: id });
      if (error) throw error;
      toast({ title: "Row queued for retry" });
      await load();
    } catch (err) {
      toast({
        title: "Retry failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>S3 Metadata Outbox</CardTitle>
          <CardDescription>
            NetSuite metadata sync queue. Failed rows have exhausted retries and won't be polled until manually retried.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-md border p-3">
            <div className="text-xs text-muted-foreground">Pending</div>
            <div className="text-2xl font-semibold">{counters.pending}</div>
          </div>
          <div className="rounded-md border p-3">
            <div className="text-xs text-muted-foreground">Failed (7d)</div>
            <div className="text-2xl font-semibold">{counters.failed7d}</div>
          </div>
          <div className="rounded-md border p-3">
            <div className="text-xs text-muted-foreground">Oldest pending</div>
            <div className="text-2xl font-semibold">
              {counters.oldestPendingAt
                ? formatDistanceToNow(new Date(counters.oldestPendingAt), { addSuffix: false })
                : "—"}
            </div>
          </div>
        </div>

        <div>
          <div className="text-sm font-medium mb-2">Recent failed rows (last 20)</div>
          {failedRows.length === 0 ? (
            <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">
              No failed rows.
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Op</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Attempts</TableHead>
                    <TableHead>Failed</TableHead>
                    <TableHead>Last error</TableHead>
                    <TableHead className="w-[100px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {failedRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{row.unique_id}</TableCell>
                      <TableCell>{row.operation}</TableCell>
                      <TableCell>{tenantSlugById[row.tenant_id] ?? "—"}</TableCell>
                      <TableCell>{row.attempt_count}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.failed_at
                          ? formatDistanceToNow(new Date(row.failed_at), { addSuffix: true })
                          : "—"}
                      </TableCell>
                      <TableCell className="max-w-[280px] truncate text-xs text-muted-foreground" title={row.last_error ?? ""}>
                        {row.last_error ?? "—"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => retry(row.id)}
                          disabled={retryingId === row.id}
                        >
                          <RotateCcw className={`h-3 w-3 mr-1 ${retryingId === row.id ? "animate-spin" : ""}`} />
                          Retry
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}